import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Menu, MenuItem, Tab, Tabs } from '@mui/material';

import Button from 'domains/core/components/Button';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE_CODE } from 'models/languages';

export { DEFAULT_LANGUAGE_CODE, SUPPORTED_LANGUAGES };

export type Props = {
    languages: string[];
    selectedLanguage: string;
    onSelectLanguage: (lang: string) => void;
    onAddLanguage: (lang: string) => void;
    onDeleteLanguage?: (lang: string) => void;
    /** When true, tab switching, adding, and deleting are all disabled (e.g. while in email preview mode). */
    disabled?: boolean;
};

const LanguageTabBar = ({
    languages,
    selectedLanguage,
    onSelectLanguage,
    onAddLanguage,
    onDeleteLanguage,
    disabled = false,
}: Props) => {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const availableToAdd = SUPPORTED_LANGUAGES.filter(({ code }) => !languages.includes(code));

    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget);
    const handleCloseMenu = () => setMenuAnchor(null);

    const handleAddLanguage = (code: string) => {
        onAddLanguage(code);
        handleCloseMenu();
    };

    const getLabelForCode = (code: string) => SUPPORTED_LANGUAGES.find((l) => l.code === code)?.label ?? code;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : undefined,
            }}
        >
            <Tabs
                aria-label="language tabs"
                value={selectedLanguage}
                onChange={(_, newLang) => onSelectLanguage(newLang)}
            >
                {languages.map((code) => (
                    <Tab
                        key={code}
                        aria-label={`${getLabelForCode(code)} language tab`}
                        data-testid={`language-tab-${code}`}
                        disabled={disabled}
                        label={
                            code !== DEFAULT_LANGUAGE_CODE && onDeleteLanguage ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {getLabelForCode(code)}
                                    <CloseIcon
                                        aria-label={`Remove ${getLabelForCode(code)} language`}
                                        data-testid={`delete-language-${code}`}
                                        sx={{ fontSize: 14, ml: 0.25 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteLanguage(code);
                                        }}
                                    />
                                </Box>
                            ) : (
                                getLabelForCode(code)
                            )
                        }
                        value={code}
                    />
                ))}
            </Tabs>
            {availableToAdd.length > 0 && !disabled && (
                <>
                    <Button
                        aria-controls={menuAnchor ? 'add-language-menu' : undefined}
                        aria-expanded={!!menuAnchor}
                        aria-haspopup="true"
                        aria-label="Add language"
                        data-testid="add-language-button"
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{ ml: 1, whiteSpace: 'nowrap' }}
                        onClick={handleOpenMenu}
                    >
                        Add Language
                    </Button>
                    <Menu anchorEl={menuAnchor} id="add-language-menu" open={!!menuAnchor} onClose={handleCloseMenu}>
                        {availableToAdd.map(({ code, label }) => (
                            <MenuItem
                                key={code}
                                data-testid={`add-language-option-${code}`}
                                onClick={() => handleAddLanguage(code)}
                            >
                                {label}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    );
};

export default LanguageTabBar;
